/**
 * Service API pour Checklist TI (Comité TI)
 * Gestion des items de checklist de préparation des réunions
 */

const API_BASE_URL = '/api/prisme/comite/checklist';

export interface ChecklistItem {
  id?: number;
  reunion_id: number;
  titre: string;
  description?: string;
  ordre?: number;
  statut: 'À Valider' | 'Non Validé';
  responsable?: string;
  est_obligatoire?: boolean;
  date_creation?: string;
  date_modification?: string;
}

export async function getChecklistItems(reunionId: number): Promise<ChecklistItem[]> {
  const response = await fetch(`${API_BASE_URL}/${reunionId}`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des items de checklist');
  }
  return response.json();
}

export async function createChecklistItem(data: Partial<ChecklistItem>): Promise<ChecklistItem> {
  const response = await fetch(API_BASE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la création de l'item de checklist");
  }

  const result = await response.json();
  // L'API retourne { success: true, id: ..., data: ... }, extraire data
  return result.data || result;
}

export async function updateChecklistItem(id: number, data: Partial<ChecklistItem>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la mise à jour de l'item de checklist");
  }
}

export async function deleteChecklistItem(id: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la suppression de l'item de checklist");
  }
}

export async function toggleChecklistItemStatus(
  id: number,
  newStatus: 'À Valider' | 'Non Validé'
): Promise<void> {
  await updateChecklistItem(id, { statut: newStatus });
}

/**
 * Créer les items de checklist par défaut pour une réunion
 */
export async function createDefaultChecklistItems(
  reunionId: number,
  responsable: string
): Promise<ChecklistItem[]> {
  // Vérifier d'abord si des items existent déjà pour éviter les doublons
  try {
    const existingItems = await getChecklistItems(reunionId);
    if (existingItems.length > 0) {
      console.log(
        `⚠️ Des items de checklist existent déjà pour la réunion ${reunionId} (${existingItems.length} items). Aucun item par défaut ne sera créé.`
      );
      return existingItems;
    }
  } catch {
    // Si erreur lors de la récupération, continuer quand même (peut-être que la table n'existe pas encore)
    console.warn("Impossible de vérifier les items existants, création des items par défaut...");
  }

  const defaultItems = [
    { titre: 'Ordre du jour préparé', ordre: 1, est_obligatoire: true },
    { titre: 'Documents nécessaires disponibles', ordre: 2, est_obligatoire: true },
    { titre: 'Salle/réunion Teams configurée', ordre: 3, est_obligatoire: true },
    { titre: 'Présentations prêtes', ordre: 4, est_obligatoire: true },
    { titre: 'Tuiles et infos au noms du client', ordre: 5, est_obligatoire: true },
  ];

  const createdItems: ChecklistItem[] = [];

  for (const item of defaultItems) {
    const created = await createChecklistItem({
      reunion_id: reunionId,
      titre: item.titre,
      ordre: item.ordre,
      statut: 'Non Validé',
      responsable,
      est_obligatoire: item.est_obligatoire,
    });
    createdItems.push(created);
  }

  return createdItems;
}

